/**
 * /projects — list of batch workspaces.
 *
 * Each project is a self-contained folder on the server with its own
 * input/, config/, output/. Clicking "New project" sets up the folder;
 * the detail page handles STEP upload + batch generation.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  Callout,
  Card,
  Dialog,
  DialogBody,
  DialogFooter,
  FormGroup,
  HTMLSelect,
  HTMLTable,
  NonIdealState,
  Spinner,
  Tag,
} from '@blueprintjs/core'

import {
  createProject,
  listProjects,
  listTemplates,
  type ProjectSummary,
  type TemplateSummary,
} from '../api'

export function ProjectsList() {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; items: ProjectSummary[] }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' })
  const [dialogOpen, setDialogOpen] = useState(false)

  const refresh = async () => {
    try {
      const items = await listProjects()
      setState({ kind: 'ok', items })
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) })
    }
  }

  useEffect(() => { refresh() }, [])

  return (
    <div className="projects-root">
      <header className="projects-header">
        <Link to="/" className="projects-back">← Workspace</Link>
        <h2>Projects</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Link to="/cells"><Button icon="build" minimal>Cells</Button></Link>
          <Button icon="add" intent="primary" onClick={() => setDialogOpen(true)}>
            New project
          </Button>
        </div>
      </header>

      {state.kind === 'loading' && <Spinner />}
      {state.kind === 'error' && (
        <Callout intent="danger" icon="error">
          {state.message}
        </Callout>
      )}
      {state.kind === 'ok' && state.items.length === 0 && (
        <NonIdealState
          icon="folder-close"
          title="No projects yet"
          description="A project is a batch workspace with its own STEPs, config and output tree."
          action={<Button intent="primary" icon="add" onClick={() => setDialogOpen(true)}>Create one</Button>}
        />
      )}
      {state.kind === 'ok' && state.items.length > 0 && (
        <Card>
          <HTMLTable striped interactive>
            <thead>
              <tr>
                <th>Name</th>
                <th>Robot</th>
                <th>Profile</th>
                <th>Postprocessor</th>
                <th>Inputs</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {state.items.map((p) => (
                <tr key={p.project_id}>
                  <td>
                    <Link to={`/projects/${p.project_id}`}>{p.name}</Link>
                  </td>
                  <td><Tag minimal>{p.robot}</Tag></td>
                  <td><Tag minimal intent="primary">{p.profile}</Tag></td>
                  <td><Tag minimal>{p.postprocessor}</Tag></td>
                  <td>{p.input_count}</td>
                  <td className="projects-dim">{p.created_at}</td>
                </tr>
              ))}
            </tbody>
          </HTMLTable>
        </Card>
      )}

      <NewProjectDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={() => { setDialogOpen(false); refresh() }}
      />
    </div>
  )
}

function NewProjectDialog({
  isOpen, onClose, onCreated,
}: {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [profile, setProfile] = useState<'worm' | 'auger'>('worm')
  const [templateId, setTemplateId] = useState<string>('')
  const [templates, setTemplates] = useState<TemplateSummary[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    // v0.6: pick a template to copy into the new project. The cell
    // library UI stays reachable via /cells but is no longer wired
    // through this dialog — templates are the forward-compatible
    // starter mechanism and sidestep retroactive-edit surprises.
    listTemplates().then((ts) => {
      setTemplates(ts)
      if (ts.length > 0 && !templateId) setTemplateId(ts[0].template_id)
    }).catch(() => {})
  }, [isOpen])

  const submit = async () => {
    setErr(null)
    setBusy(true)
    try {
      await createProject({
        name,
        profile,
        robot: 'ABB_IRB4600_40_255',
        postprocessor: 'abb_rapid_irc5',
        from_template: templateId || null,
      })
      setName('')
      onCreated()
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="New project" icon="folder-new">
      <DialogBody>
        <FormGroup label="Name" labelFor="np-name">
          <input
            id="np-name"
            className="bp5-input bp5-fill"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. ShopFloor_Q4"
          />
        </FormGroup>
        <FormGroup label="Auto-select profile">
          <HTMLSelect
            fill
            value={profile}
            onChange={(e) => setProfile(e.target.value as 'worm' | 'auger')}
            options={[
              { label: 'Worm (default)', value: 'worm' },
              { label: 'Auger', value: 'auger' },
            ]}
          />
        </FormGroup>
        <FormGroup
          label="Template"
          helperText="Copied into the new project's cell.yaml + params.yaml. After copy, no link — editing the template won't retroactively change this project."
        >
          <HTMLSelect
            fill
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            options={[
              { label: '— defaults (no template) —', value: '' },
              ...templates.map((t) => ({
                label: `${t.name}${t.builtin ? '  (builtin)' : ''}`,
                value: t.template_id,
              })),
            ]}
          />
        </FormGroup>
        {err && <Callout intent="danger" icon="error">{err}</Callout>}
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button intent="primary" onClick={submit} loading={busy} disabled={!name || busy}>
              Create
            </Button>
          </>
        }
      />
    </Dialog>
  )
}
